/**
 * PostHog before_send hook that allowlists events and app-passed properties
 * before they leave the browser. Unknown events are dropped; unknown app
 * properties are stripped; SDK metadata passes through except URL fields,
 * which have query strings and hashes removed to avoid PII in page URLs.
 */

import {
	ALLOWED_APP_PROPERTIES,
	ALLOWED_EVENTS,
	ALLOWED_GROUP_PROPERTIES,
	POSTHOG_SDK_PROPERTIES,
} from "./posthogAllowlist";
import {
	POSTHOG_URL_PROPERTIES,
	stripUrlSearchAndHash,
} from "./stripPostHogUrlPii";

function filterPrimitivePropertyValue(value: unknown): unknown {
	const valueType = typeof value;
	if (
		valueType === "string" ||
		valueType === "number" ||
		valueType === "boolean" ||
		value === null ||
		value === undefined
	) {
		return value;
	}

	return "[non-primitive]";
}

function filterGroupProperties(
	properties: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
	if (!properties) {
		return {};
	}

	const filtered: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(properties)) {
		if (ALLOWED_GROUP_PROPERTIES.has(key)) {
			filtered[key] = filterPrimitivePropertyValue(value);
		}
	}

	return filtered;
}

function filterAppProperties(
	properties: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
	if (!properties) {
		return {};
	}

	const filtered: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(properties)) {
		if (key.startsWith("$")) {
			if (key === "$group_set" && value && typeof value === "object") {
				filtered[key] = filterGroupProperties(value as Record<string, unknown>);
				continue;
			}

			if (typeof value === "string" && POSTHOG_URL_PROPERTIES.has(key)) {
				filtered[key] = stripUrlSearchAndHash(value);
			} else {
				filtered[key] = value;
			}
			continue;
		}

		if (POSTHOG_SDK_PROPERTIES.has(key) || ALLOWED_APP_PROPERTIES.has(key)) {
			filtered[key] = filterPrimitivePropertyValue(value);
		}
	}

	return filtered;
}

type PostHogCaptureResult = {
	uuid: string;
	event: string;
	properties: Record<string, unknown>;
	$set?: Record<string, unknown>;
	$set_once?: Record<string, unknown>;
	$group_set?: Record<string, unknown>;
	timestamp?: Date;
};

export function sanitizePostHogEvent(
	event: PostHogCaptureResult | null,
): PostHogCaptureResult | null {
	if (!event) {
		return null;
	}

	if (!ALLOWED_EVENTS.has(event.event)) {
		return null;
	}

	const sanitized = { ...event };

	sanitized.properties = filterAppProperties(event.properties);

	if (event.$set) {
		sanitized.$set = filterAppProperties(event.$set);
	}
	if (event.$set_once) {
		sanitized.$set_once = filterAppProperties(event.$set_once);
	}
	if (event.$group_set) {
		sanitized.$group_set = filterGroupProperties(event.$group_set);
	}

	return sanitized;
}
